import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Shield, Trash2, Plus, Users, MessagesSquare, MessageCircle, Ban, CheckCircle, Flag } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(null);

  useEffect(() => {
    if (!loading && (!user || (user.role !== "admin" && user.role !== "moderator"))) nav("/");
    if (user && !tab) setTab(user.role === "admin" ? "dashboard" : "reports");
  }, [user, loading, nav, tab]);

  if (!user || (user.role !== "admin" && user.role !== "moderator") || !tab) return null;
  const isAdmin = user.role === "admin";

  return (
    <div className="space-y-5 font-eng" dir="ltr" data-testid="admin-page">
      <div className="flex items-center gap-3 border-b-2 border-primary pb-3">
        <Shield className="text-primary" size={22} />
        <h1 className="text-3xl font-bold text-primary tracking-tight">Admin Control Panel</h1>
        <span className="text-xs text-muted-foreground ml-auto">Signed in as {user.username}</span>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="admin-tabs">
          {isAdmin && <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>}
          {isAdmin && <TabsTrigger value="structure" data-testid="tab-structure">Forums & Categories</TabsTrigger>}
          {isAdmin && <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>}
          <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
        </TabsList>
        {isAdmin && <TabsContent value="dashboard"><Dashboard /></TabsContent>}
        {isAdmin && <TabsContent value="structure"><Structure /></TabsContent>}
        {isAdmin && <TabsContent value="users"><UsersPanel /></TabsContent>}
        <TabsContent value="reports"><ReportsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function ReportsPanel() {
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = useCallback(() => api.get(`/mod/reports?status=${status}`).then((r) => setReports(r.data.reports)), [status]);
  useEffect(() => { load(); }, [load]);
  const act = async (rid, action) => {
    await api.post(`/mod/reports/${rid}/resolve`, { action });
    toast.success(action === "resolve" ? "Resolved" : "Dismissed");
    load();
  };
  return (
    <Card className="p-5 mt-4">
      <div className="flex items-center gap-3 mb-3">
        <Flag size={18} className="text-destructive" />
        <h3 className="font-semibold">Post reports</h3>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 ml-auto" data-testid="reports-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {reports.length === 0 && <div className="text-center py-8 text-muted-foreground text-sm">— No reports</div>}
      <div className="space-y-3">
        {reports.map((r) => (
          <div key={r.id} className="border border-border rounded-lg p-4" data-testid={`report-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-xs text-muted-foreground">
                  <b>{r.reporter?.username}</b> reported a post by <b>{r.post?.author?.username}</b> in
                  {" "}<a href={`/t/${r.thread?.id}#post-${r.post_id}`} target="_blank" rel="noreferrer" className="text-primary underline"><span className="urdu-naskh">{r.thread?.title}</span></a>
                </div>
                <div className="mt-2 text-sm italic urdu-naskh">&ldquo;{r.reason}&rdquo;</div>
                {r.post && (
                  <div className="mt-2 p-3 rounded bg-muted urdu-naskh text-sm text-muted-foreground line-clamp-3"
                       dangerouslySetInnerHTML={{ __html: r.post.content }} />
                )}
              </div>
              {status === "pending" && (
                <div className="flex flex-col gap-1">
                  <Button size="sm" onClick={() => act(r.id, "warn")} className="bg-amber-600 hover:bg-amber-700 gap-1" data-testid={`report-warn-${r.id}`}>⚠ Warn</Button>
                  <Button size="sm" onClick={() => act(r.id, "resolve")} className="bg-destructive gap-1" data-testid={`report-resolve-${r.id}`}><CheckCircle size={14} /> Resolve</Button>
                  <Button size="sm" variant="outline" onClick={() => act(r.id, "dismiss")} data-testid={`report-dismiss-${r.id}`}>Dismiss</Button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div>Loading…</div>;
  const cards = [
    { icon: <Users size={18} />, label: "Users", value: s.users },
    { icon: <MessagesSquare size={18} />, label: "Threads", value: s.threads },
    { icon: <MessageCircle size={18} />, label: "Posts", value: s.posts },
    { icon: <Shield size={18} />, label: "Categories", value: s.categories },
    { icon: <MessagesSquare size={18} />, label: "Forums", value: s.forums },
    { icon: <Ban size={18} />, label: "Banned", value: s.banned },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
      {cards.map((c) => (
        <Card key={c.label} className="p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center">{c.icon}</div>
          <div>
            <div className="text-xs text-muted-foreground">{c.label}</div>
            <div className="text-2xl font-bold">{c.value}</div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function Structure() {
  const [cats, setCats] = useState([]);
  const [forums, setForums] = useState([]);
  const [newCat, setNewCat] = useState({ name: "", description: "", order: 0 });
  const [newForum, setNewForum] = useState({ category_id: "", name: "", description: "", order: 0, parent_forum_id: null });

  const load = async () => {
    const r = await api.get("/index");
    setCats(r.data.categories || []);
    const all = [];
    Object.values(r.data.forums_by_category || {}).forEach((arr) => arr.forEach((f) => all.push(f)));
    setForums(all);
  };
  useEffect(() => { load(); }, []);

  const createCat = async () => {
    if (!newCat.name) return toast.error("Name required");
    await api.post("/admin/categories", newCat);
    toast.success("Category created");
    setNewCat({ name: "", description: "", order: 0 });
    load();
  };
  const deleteCat = async (id) => {
    if (!window.confirm("Delete category and ALL its forums/threads/posts?")) return;
    await api.delete(`/admin/categories/${id}`);
    toast.success("Deleted");
    load();
  };
  const createForum = async () => {
    if (!newForum.category_id || !newForum.name) return toast.error("Category and name required");
    await api.post("/admin/forums", newForum);
    toast.success("Forum created");
    setNewForum({ category_id: "", name: "", description: "", order: 0, parent_forum_id: null });
    load();
  };
  const deleteForum = async (id) => {
    if (!window.confirm("Delete forum and all its threads/posts?")) return;
    await api.delete(`/admin/forums/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="grid md:grid-cols-2 gap-5 mt-4">
      <Card className="p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><Shield size={16} /> Categories</h3>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Order</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {cats.map((c) => (
              <TableRow key={c.id}>
                <TableCell><span className="urdu-naskh">{c.name}</span></TableCell>
                <TableCell>{c.order}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="destructive" onClick={() => deleteCat(c.id)} data-testid={`del-cat-${c.id}`}><Trash2 size={14} /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="border-t border-border pt-4 mt-4 space-y-2">
          <Label>New category</Label>
          <Input placeholder="Name (Urdu)" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} data-testid="new-cat-name" />
          <Textarea placeholder="Description" rows={2} value={newCat.description} onChange={(e) => setNewCat({ ...newCat, description: e.target.value })} />
          <Input type="number" placeholder="Order" value={newCat.order} onChange={(e) => setNewCat({ ...newCat, order: parseInt(e.target.value || 0) })} />
          <Button onClick={createCat} className="bg-primary gap-1" data-testid="create-cat"><Plus size={14} /> Create</Button>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold mb-3 flex items-center gap-2"><MessagesSquare size={16} /> Forums</h3>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Visibility</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {forums.map((f) => (
              <TableRow key={f.id}>
                <TableCell><span className="urdu-naskh">{f.name}</span>{f.parent_forum_id && <span className="text-[10px] text-muted-foreground ms-1">↳ sub</span>}</TableCell>
                <TableCell className="text-xs"><span className="urdu-naskh">{cats.find((c) => c.id === f.category_id)?.name}</span></TableCell>
                <TableCell>
                  <Select value={f.visibility || "public"} onValueChange={async (v) => {
                    await api.put(`/admin/forums/${f.id}/visibility`, { visibility: v });
                    toast.success("Visibility updated");
                    load();
                  }}>
                    <SelectTrigger className="w-32 h-8 text-xs" data-testid={`vis-${f.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public</SelectItem>
                      <SelectItem value="members">Members</SelectItem>
                      <SelectItem value="moderators">Moderators</SelectItem>
                      <SelectItem value="admins">Admins</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="destructive" onClick={() => deleteForum(f.id)} data-testid={`del-forum-${f.id}`}><Trash2 size={14} /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="border-t border-border pt-4 mt-4 space-y-2">
          <Label>New forum</Label>
          <Select value={newForum.category_id} onValueChange={(v) => setNewForum({ ...newForum, category_id: v })}>
            <SelectTrigger data-testid="new-forum-category"><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>
              {cats.map((c) => <SelectItem key={c.id} value={c.id}><span className="urdu-naskh">{c.name}</span></SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={newForum.parent_forum_id || "__none__"} onValueChange={(v) => setNewForum({ ...newForum, parent_forum_id: v === "__none__" ? null : v })}>
            <SelectTrigger data-testid="new-forum-parent"><SelectValue placeholder="Parent forum (optional — leave empty for top-level)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— No parent (top-level) —</SelectItem>
              {forums.filter((f) => !f.parent_forum_id && (!newForum.category_id || f.category_id === newForum.category_id)).map((f) => (
                <SelectItem key={f.id} value={f.id}><span className="urdu-naskh">{f.name}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input placeholder="Forum name (Urdu)" value={newForum.name} onChange={(e) => setNewForum({ ...newForum, name: e.target.value })} data-testid="new-forum-name" />
          <Textarea placeholder="Description" rows={2} value={newForum.description} onChange={(e) => setNewForum({ ...newForum, description: e.target.value })} />
          <Input type="number" placeholder="Order" value={newForum.order} onChange={(e) => setNewForum({ ...newForum, order: parseInt(e.target.value || 0) })} />
          <Button onClick={createForum} className="bg-primary gap-1" data-testid="create-forum"><Plus size={14} /> Create</Button>
        </div>
      </Card>
    </div>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const load = async () => {
    const r = await api.get(`/admin/users${q ? `?search=${encodeURIComponent(q)}` : ""}`);
    setUsers(r.data.users);
  };
  // Initial load only — subsequent loads are explicit (search button / Enter)
  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(r.data.users));
  }, []);
  const setRole = async (id, role) => {
    await api.put(`/admin/users/${id}/role`, { role });
    toast.success("Role updated");
    load();
  };
  const toggleBan = async (u) => {
    await api.put(`/admin/users/${u.id}/ban`, { is_banned: !u.is_banned });
    toast.success(!u.is_banned ? "User banned" : "User unbanned");
    load();
  };
  return (
    <Card className="p-5 mt-4">
      <div className="flex gap-2 items-center mb-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search by email or username" data-testid="admin-users-search" />
        <Button onClick={load}>Search</Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead>Posts</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.id}>
              <TableCell className="font-semibold">{u.username}</TableCell>
              <TableCell className="text-xs">{u.email}</TableCell>
              <TableCell className="text-xs">{fmtDate(u.created_at)}</TableCell>
              <TableCell>{u.post_count}</TableCell>
              <TableCell>
                <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
                  <SelectTrigger className="w-32" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="moderator">Moderator</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                {u.is_banned
                  ? <Badge variant="destructive">Banned</Badge>
                  : <Badge variant="secondary">Active</Badge>}
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant={u.is_banned ? "outline" : "destructive"} onClick={() => toggleBan(u)} data-testid={`ban-${u.id}`}>
                  {u.is_banned ? <><CheckCircle size={14} /> Unban</> : <><Ban size={14} /> Ban</>}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
